import {
  hasConsentForServices,
  initializePluginWithConsent,
  ServiceIds,
} from 'chimera/all/plugins/config/usercentrics.client'
import { EventBus } from 'chimera/all/plugins/eventbus'

const SIDECAR_URL =
  'https://cdn.jsdelivr.net/npm/statsig-sidecar-v2-beta/dist/index.min.js'
const ANTI_FLICKER_TIMEOUT = 3000
const ANTI_FLICKER_ID = '__sidecar_antiflicker'
const AD_BLOCKER_DETECT_TIMEOUT = 500
const MAX_RETRY_COUNT = 1
const RETRY_DELAY = 1000

let sidecarLoaded = false

/**
 * Detect if an ad blocker is likely blocking the Statsig script
 * @returns {Promise<boolean>} True if ad blocker suspected
 */
async function detectAdBlocker() {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(
      () => controller.abort(),
      AD_BLOCKER_DETECT_TIMEOUT,
    )

    await fetch(SIDECAR_URL, {
      method: 'HEAD',
      mode: 'no-cors',
      signal: controller.signal,
    })

    clearTimeout(timeoutId)
    return false
  } catch {
    return true
  }
}

/**
 * Clear anti-flicker style if present
 */
function clearAntiFlicker() {
  document.getElementById(ANTI_FLICKER_ID)?.remove()
}

/**
 * Bridge Visual Editor experiments to Vuex store
 * @param {object} store - Vuex store instance
 */
function setupExperimentBridge(store) {
  if (!store) {
    return
  }

  window.postExperimentCallback = () => {
    clearAntiFlicker()
    window.dispatchEvent(new CustomEvent('experimentation:ready'))

    const experiments = window.StatsigSidecar?._getMatchingExperiments?.() || []
    experiments.forEach((exp) => {
      const id = exp?.value?.sidecarExperimentName || exp?.name
      if (id) store.dispatch('experiments/get', { id, isVisualEditor: true })
    })
  }
}

/**
 * Determine the failure reason based on diagnostics
 * @param {boolean} suspectedAdBlocker - Whether ad blocker was detected
 * @param {boolean} isTimeout - Whether this was a timeout failure
 * @returns {string} Failure reason identifier
 */
function getFailureReason(suspectedAdBlocker, isTimeout) {
  if (!navigator.onLine) {
    return 'offline'
  }
  if (suspectedAdBlocker) {
    return 'ad_blocker'
  }
  if (isTimeout) {
    return 'timeout'
  }
  return 'network_error'
}

/**
 * Load Statsig Sidecar script with anti-flicker protection
 * @param {string} clientKey - Statsig client key
 * @param {object} store - Vuex store instance
 * @param {string} environment - Environment name (local, acceptance, production)
 * @param {number} retryCount - Current retry attempt count
 */
async function loadSidecarScript(
  clientKey,
  store,
  environment,
  retryCount = 0,
) {
  if (sidecarLoaded) {
    return
  }
  sidecarLoaded = true

  // Configure Statsig user with visitor ID
  const visitorId = store?.getters?.['context/get']?.('visitorId')
  if (visitorId) {
    window.statsigUser = { customIDs: { 'Visitor ID': visitorId } }
  }

  // Configure Statsig options with environment tier
  if (environment) {
    window.statsigOptions = { environment: { tier: environment } }
  }

  // Detect ad blocker before attempting to load
  const suspectedAdBlocker = await detectAdBlocker()

  // Anti-flicker: hide body until experiments are applied
  const style = document.createElement('style')
  style.id = ANTI_FLICKER_ID
  style.textContent = 'body{opacity:0!important}'
  document.head.appendChild(style)

  let isTimeout = false
  const timeout = setTimeout(() => {
    isTimeout = true
    clearAntiFlicker()
  }, ANTI_FLICKER_TIMEOUT)

  // Load script
  const script = document.createElement('script')
  script.src = `${SIDECAR_URL}?apikey=${clientKey}&autocapture=0&reduceflicker=0`
  script.onload = () => clearTimeout(timeout)
  script.onerror = (event) => {
    clearTimeout(timeout)
    clearAntiFlicker()

    const failureReason = getFailureReason(suspectedAdBlocker, isTimeout)

    // Retry once for network errors (not ad blockers or offline)
    if (failureReason === 'network_error' && retryCount < MAX_RETRY_COUNT) {
      sidecarLoaded = false
      setTimeout(() => {
        loadSidecarScript(clientKey, store, environment, retryCount + 1)
      }, RETRY_DELAY)
      return
    }

    EventBus.emitWarningAppErrorEvent(
      new Error(`Failed to load Statsig Sidecar script: ${failureReason}`),
      { context: 'statsig-sidecar/install' },
    )
  }
  document.head.appendChild(script)
}

/**
 * Initialize Statsig Sidecar for Visual Editor experiments
 * @param {object} context - Nuxt plugin context
 * @param {object} context.env - Environment variables
 * @param {object} context.store - Vuex store
 */
export default ({ env, store }) => {
  const clientKey = env.statsigVisualEditorClientKey
  if (!clientKey) {
    return
  }

  setupExperimentBridge(store)

  const load = () => loadSidecarScript(clientKey, store, env.environment)
  const services = [ServiceIds.STATSIG]

  hasConsentForServices(services).then((ok) => ok && load())
  initializePluginWithConsent(load, services)
}
